<script lang="ts">
    import { pop } from "svelte-spa-router";
    import { fade } from "svelte/transition";
    import ajax from "../ajax/ajax";
    import Button from "../components/buttons/Button.svelte";
    import IconButton from "../components/buttons/IconButton.svelte";
    import InlineCheckboxes from "../components/form/input/InlineCheckboxes.svelte";
    import TextInput from "../components/form/input/TextInput.svelte";
    import BackIcon from "../components/icons/BackIcon.svelte";
    import MainMenu from "../components/menu/MainMenu.svelte";
    import Paper from "../components/paper/Paper.svelte";
    import Snackbar from "../components/snackbar/Snackbar.svelte";

    export let params: { projectId: number };

    type TUser = {
        id: number;
        name: string;
    };

    type TProjectData = {
        project: { id: number; name: string; userId: number };
        users: TUser[];
    };
    let loading = true;
    let loadingUsers = true;
    let users: TUser[];
    let message: { type: "error" | "success"; message: string } | null = null;

    let projectName = "";
    let projectOwnerId: string = "";
    let selectedUserIds: string[] = [];

    function getErrorFromAjaxReturn(
        statusText: string,
        status: number,
        xhr: XMLHttpRequest
    ): {
        type: "error";
        message: string;
    } {
        try {
            const returnValue = JSON.parse(xhr.responseText);
            if (returnValue) {
                return { type: "error", message: returnValue.errorMessage };
            }
        } catch {}

        return { type: "error", message: statusText };
    }

    ajax.get<TProjectData>(
        `/projects/${params.projectId}`,
        (jsonData) => {
            loading = false;
            projectName = jsonData.project.name;
            projectOwnerId = jsonData.project.userId.toString();
            selectedUserIds = jsonData.users.map((user) => {
                return user.id.toString();
            });
        },
        (statusText, status, xhr) => {
            loading = false;
            message = getErrorFromAjaxReturn(statusText, status, xhr);
        }
    );

    ajax.get<{ users: TUser[] }>(
        `/users`,
        (jsonData) => {
            users = jsonData.users;
            loadingUsers = false;
        },
        (statusText, status, xhr) => {
            loadingUsers = false;
            message = getErrorFromAjaxReturn(statusText, status, xhr);
        }
    );

    function updateCheckedValues(checkedValues: string[]) {
        if (checkedValues.indexOf(projectOwnerId) === -1) {
            selectedUserIds = [...checkedValues, projectOwnerId];
        } else {
            selectedUserIds = [...checkedValues];
        }
    }

    function save() {
        ajax.post(
            `/projects/${params.projectId}`,
            { name: projectName, userIds: selectedUserIds },
            () => {
                message = { type: "success", message: "Project was updated." };
            },
            (statusText, status, xhr) => {
                message = getErrorFromAjaxReturn(statusText, status, xhr);
            }
        );
    }
</script>

<MainMenu />
<div class="ProjectSettings">
    <div style="margin-bottom: 20px">
        <IconButton
            icon={BackIcon}
            title="Back"
            onClick={() => {
                pop();
            }}
            width="24px"
            height="24px"
        />
    </div>
    <Paper>
        <h1>Project settings</h1>
        {#if loading || loadingUsers}
            Loading...
        {:else}
            <form on:submit={save}>
                <TextInput
                    label="Project name"
                    value={projectName}
                    onchange={(value) => {
                        projectName = value;
                    }}
                />
                <InlineCheckboxes
                    label="Users"
                    checkboxes={users.map((user) => {
                        return { label: user.name, value: user.id.toString() };
                    })}
                    checkedValues={selectedUserIds}
                    onChange={updateCheckedValues}
                />
                <Button label="Update" color="primary" onclick={save} />
            </form>
        {/if}
    </Paper>
</div>

{#if message}
    <div in:fade={{ duration: 300 }} out:fade>
        <Snackbar message={message.message} type={message.type} />
    </div>
{/if}

<style type="text/scss">
    h1 {
        font-size: 1.5rem;
        line-height: 1.5rem;
        margin: 0 0 1.5rem 0;
    }

    .ProjectSettings {
        padding: 50px;
        :global(.form-item) {
            margin-bottom: 25px;
        }
    }
</style>
